import React from "react";
import { brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Minus, Trash2, ShoppingCart, Banknote, CreditCard, QrCode, UserCircle2, Clock } from "lucide-react";
import { isLinear } from "@/lib/linearPricing";

export const PAYMENT_METHODS = [
  { key: "cash", label: "Dinheiro", icon: Banknote, tid: "payment-cash" },
  { key: "credit", label: "Crédito", icon: CreditCard, tid: "payment-credit" },
  { key: "debit", label: "Débito", icon: CreditCard, tid: "payment-debit" },
  { key: "pix", label: "PIX", icon: QrCode, tid: "payment-pix" },
  { key: "credit_term", label: "Prazo", icon: Clock, tid: "payment-credit_term" },
];

const ProductCard = ({ p, onAdd }) => (
  <button onClick={() => onAdd(p)} data-testid={`product-card-${p.id}`}
    className="card-outline text-left overflow-hidden hover:border-blue-500 hover:shadow-md transition group">
    {p.image ? (
      <img src={p.image} alt={p.name} className="w-full h-24 object-cover" />
    ) : (
      <div className="w-full h-24 bg-slate-100 flex items-center justify-center text-slate-400">
        <ShoppingCart className="w-6 h-6" />
      </div>
    )}
    <div className="p-3">
      <div className="text-sm font-semibold text-slate-900 line-clamp-1">{p.name}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{p.category || p.type}</div>
      <div className="flex items-center justify-between mt-2">
        <div className="kpi-num text-blue-600">{isLinear(p) ? `a partir de ${brl(p.price)}/m` : brl(p.price)}</div>
        {p.type === "product" && (
          <Badge variant="outline" className={"text-[10px] " + (p.stock < p.min_stock ? "border-rose-500 text-rose-600" : "")}>
            {p.stock} un
          </Badge>
        )}
      </div>
    </div>
  </button>
);

export const ProductGrid = ({ products, onAdd }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
    {products.map((p) => <ProductCard key={p.id} p={p} onAdd={onAdd} />)}
    {products.length === 0 && (
      <div className="col-span-full text-slate-400 text-center py-12">Nenhum produto encontrado.</div>
    )}
  </div>
);

const CartLine = ({ i, onDelta, onQty, onBlurQty, onRemove }) => {
  const key = i.line_id || i.product_id;
  const hide = i.linear ? "hidden" : "";
  return (
    <div className="flex items-center gap-2 border-b border-slate-100 pb-2 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{i.name}</div>
        <div className="text-xs text-slate-500 kpi-num">{i.linear ? `${i.quantity} m • ${brl(i.price * i.quantity)}` : `${brl(i.price)} × ${i.quantity}`}</div>
      </div>
      <div className="flex items-center gap-1">
        <Button size="icon" variant="outline" className={`h-7 w-7 ${hide}`} data-testid={`qty-minus-${i.product_id}`} onClick={() => onDelta(i.product_id, -1)}>
          <Minus className="w-3 h-3" />
        </Button>
        {!i.linear && <Input type="number" min="0" step="any" inputMode="decimal" value={i.quantity}
          onChange={(e) => onQty(i.product_id, e.target.value)}
          onBlur={() => onBlurQty(i.product_id)}
          onFocus={(e) => e.target.select()}
          className="h-7 w-16 px-1 text-center text-sm kpi-num" data-testid={`qty-input-${i.product_id}`} />}
        <Button size="icon" variant="outline" className={`h-7 w-7 ${hide}`} data-testid={`qty-plus-${i.product_id}`} onClick={() => onDelta(i.product_id, 1)}>
          <Plus className="w-3 h-3" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" data-testid={`remove-${key}`} onClick={() => onRemove(key)}>
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

export const CartItems = ({ cart, ...handlers }) => (
  <div className="max-h-64 overflow-y-auto space-y-2 -mx-1 px-1">
    {cart.length === 0 && <div className="text-slate-400 text-sm text-center py-8">Carrinho vazio</div>}
    {cart.map((i) => <CartLine key={i.line_id || i.product_id} i={i} {...handlers} />)}
  </div>
);

export const PaymentMethods = ({ payment, setPayment }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider text-slate-500">Forma de pagamento</Label>
    <div className="grid grid-cols-2 gap-2 mt-2">
      {PAYMENT_METHODS.map((m) => (
        <button key={m.key} type="button" data-testid={m.tid} onClick={() => setPayment(m.key)}
          className={"px-3 py-2 rounded-lg border text-sm flex items-center gap-2 transition " +
            (payment === m.key ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-slate-400")}>
          <m.icon className="w-4 h-4" /> {m.label}
        </button>
      ))}
    </div>
  </div>
);

export const PaymentDetails = ({ payment, f }) => (
  <>
    {payment === "credit_term" && (
      <div className="space-y-2 border rounded-lg p-3 bg-amber-50">
        <Label className="text-xs">Primeiro vencimento</Label>
        <Input type="date" value={f.dueDate} onChange={(e) => f.setDueDate(e.target.value)} data-testid="credit-due-input" />
        <Label className="text-xs">Parcelas (mensais)</Label>
        <Input type="number" min="1" value={f.installments} onChange={(e) => f.setInstallments(e.target.value)} data-testid="credit-installments-input" />
        <div className="text-xs text-amber-700">Será criada 1 conta a receber por parcela.</div>
      </div>
    )}
    {payment === "cash" && (
      <div>
        <Label className="text-xs">Valor recebido</Label>
        <Input type="number" step="0.01" value={f.cashReceived} onChange={(e) => f.setCashReceived(e.target.value)}
          data-testid="cash-received-input" placeholder="0,00" />
        {f.cashReceived && <div className="text-xs text-emerald-600 mt-1">Troco: <span className="kpi-num">{brl(f.change)}</span></div>}
      </div>
    )}
  </>
);

export const CustomerFields = ({ customers, customer, setCustomer, customerId, setCustomerId }) => (
  <>
    <Input placeholder="Cliente (opcional)" value={customer} onChange={(e) => setCustomer(e.target.value)} data-testid="customer-input" />
    {customers.length > 0 && (
      <Select value={customerId || "none"} onValueChange={(v) => setCustomerId(v === "none" ? "" : v)}>
        <SelectTrigger data-testid="customer-select">
          <div className="flex items-center gap-2"><UserCircle2 className="w-4 h-4" /><SelectValue placeholder="Vincular cliente cadastrado" /></div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Sem cliente vinculado</SelectItem>
          {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
    )}
  </>
);
